import { motion, type Variants, type Transition } from "motion/react";
import type { ReactNode, CSSProperties } from "react";

const EASE = [0.25, 0.1, 0.25, 1] as const;

/**
 * SlideUpLine
 * Wraps children in an overflow-hidden inline-block span; inner motion.span
 * slides from y:"100%" to y:"0%". Use for single-line headings, eyebrow text, etc.
 */
export function SlideUpLine({
  children,
  delay = 0,
  duration = 0.7,
  className = "",
  as = "span",
}: {
  children: ReactNode;
  delay?: number;
  duration?: number;
  className?: string;
  as?: "span" | "div";
}) {
  const Wrapper = as === "div" ? motion.div : motion.span;
  return (
    <Wrapper
      className={`overflow-hidden ${
        as === "div" ? "block" : "inline-block align-bottom"
      } ${className}`}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
    >
      <motion.span
        className={as === "div" ? "block" : "inline-block"}
        variants={{
          hidden: { y: "100%" },
          visible: { y: "0%" },
        }}
        transition={{ duration, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </Wrapper>
  );
}

/**
 * WordByWordReveal
 * Splits text into words; each word wrapped in overflow-hidden inline-block
 * span; inner span animates y:"100%" -> y:"0%" with stagger.
 */
export function WordByWordReveal({
  text,
  delay = 0,
  stagger = 0.035,
  duration = 0.55,
  className = "",
  highlight,
  highlightClassName = "text-crest-orange",
}: {
  text: string;
  delay?: number;
  stagger?: number;
  duration?: number;
  className?: string;
  highlight?: string;
  highlightClassName?: string;
}) {
  const words = text.split(" ");
  const container: Variants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: stagger, delayChildren: delay },
    },
  };
  const child: Variants = {
    hidden: { y: "100%" },
    visible: { y: "0%" },
  };
  const transition: Transition = { duration, ease: EASE };

  return (
    <motion.span
      className={`inline ${className}`}
      variants={container}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
    >
      {words.map((w, i) => {
        const isHighlight =
          highlight &&
          w.toLowerCase().replace(/[^a-z]/g, "") ===
            highlight.toLowerCase().replace(/[^a-z]/g, "");
        return (
          <span
            key={`${w}-${i}`}
            className="overflow-hidden inline-block align-bottom"
          >
            <motion.span
              className={`inline-block ${
                isHighlight ? highlightClassName : ""
              }`}
              variants={child}
              transition={transition}
            >
              {w}
              {i < words.length - 1 ? "\u00A0" : ""}
            </motion.span>
          </span>
        );
      })}
    </motion.span>
  );
}

/**
 * FadeUp
 * Simple fade + slide up for cards, paragraphs, images.
 */
export function FadeUp({
  children,
  delay = 0,
  duration = 0.6,
  y = 24,
  className = "",
  once = true,
  amount = 0.2,
  style,
}: {
  children: ReactNode;
  delay?: number;
  duration?: number;
  y?: number;
  className?: string;
  once?: boolean;
  amount?: number;
  style?: CSSProperties;
}) {
  return (
    <motion.div
      className={className}
      style={style}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount }}
      transition={{ duration, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

/**
 * Stagger
 * Container for staggered children FadeUp animations.
 */
export function Stagger({
  children,
  delay = 0,
  stagger = 0.08,
  className = "",
  amount = 0.15,
}: {
  children: ReactNode;
  delay?: number;
  stagger?: number;
  className?: string;
  amount?: number;
}) {
  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount }}
      variants={{
        hidden: {},
        visible: {
          transition: { staggerChildren: stagger, delayChildren: delay },
        },
      }}
    >
      {children}
    </motion.div>
  );
}

/**
 * StaggerItem
 * Use inside Stagger. Animates opacity + y from variants.
 */
export function StaggerItem({
  children,
  y = 24,
  duration = 0.55,
  className = "",
  style,
}: {
  children: ReactNode;
  y?: number;
  duration?: number;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <motion.div
      className={className}
      style={style}
      variants={{
        hidden: { opacity: 0, y },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration, ease: EASE },
        },
      }}
    >
      {children}
    </motion.div>
  );
}

/**
 * ScaleIn
 * Subtle scale + fade for images/cards.
 */
export function ScaleIn({
  children,
  delay = 0,
  duration = 0.8,
  className = "",
  style,
}: {
  children: ReactNode;
  delay?: number;
  duration?: number;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <motion.div
      className={className}
      style={style}
      initial={{ opacity: 0, scale: 1.06 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}